import React, { useEffect, useState } from 'react';

import { captureException } from '@sentry/nextjs';
import config from 'config';
import { NextPage, NextPageContext } from 'next';
import type { ErrorProps } from 'next/error';
import NextErrorComponent from 'next/error';
import { useRouter } from 'next/router';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import createPage from '../registry';
import {
  errorPageRendered,
  trackErrorPageRendered,
} from '../src/actions/pagesRendered';
import { Component as CheckoutTemplate } from '../src/components/CheckoutTemplate';
import { Component as ErrorPageLayout } from '../src/components/ErrorPageLayout';
import { useKeyPress } from '../src/utils/useKeyPress';

const displayProductionErrors = config.get('public.displayProductionErrors');

const StyledCheckoutTemplate = styled(CheckoutTemplate)`
  background: ${props => props.theme.colors.lavender100};
  position: relative;
  pointer-events: none;

  &:after {
    content: ' ';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: ${props => props.theme.zIndex.errorOverlay};
  }

  > ${CheckoutTemplate.Body} {
    align-items: center;
    display: flex;
    justify-content: center;
    pointer-events: all;
    z-index: ${props => props.theme.zIndex.errorOverlay + 1};
  }
`;

const ErrorHeader = styled.div`
  display: flex;
  align-items: center;
`;

const ErrorMessage = styled.span`
  font-size: 24px;
  color: ${({ theme }) => theme.colors.error};
  padding: ${({ theme }) => theme.spacing.tiny}px;
`;

const StatusCode = styled.span`
  font-size: 24px;
  font-weight: 600;
  padding-right: 5px;
  border-right: 2px solid ${({ theme }) => theme.colors.default};
`;

const StackTrace = styled.pre`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 2px 0 0 10px;
`;

type BaseErrorStackProps = {
  errorProps: ErrorPageProps;
};

function BaseErrorStack({ errorProps, ...rest }: BaseErrorStackProps) {
  const { message, stack, statusCode } = errorProps;
  const showHeader = Boolean(message || statusCode);

  return (
    <div {...rest}>
      {showHeader ? (
        <ErrorHeader>
          {statusCode ? <StatusCode>{statusCode}</StatusCode> : null}
          {message ? <ErrorMessage>{message}</ErrorMessage> : null}
        </ErrorHeader>
      ) : null}
      <StackTrace>{stack}</StackTrace>
    </div>
  );
}

const ErrorStack = styled(BaseErrorStack)`
  margin: auto;
  padding-top: 100px;
  width: fit-content;
`;

interface ErrorPageProps extends ErrorProps {
  message: string;
  stack: string;
  statusCode: number;
}

const ErrorPage: NextPage<ErrorPageProps> = props => {
  const router = useRouter();
  const dispatch = useDispatch();
  const [showError, setShowError] = useState(false);

  useKeyPress('e', () => {
    setShowError(!showError);
  });

  useEffect(() => {
    // Next.js does not handle the error page throwing its own errors very
    // gracefully, causing an infinite loop, so prevent that here.
    try {
      dispatch(errorPageRendered(router, props.statusCode));
      dispatch(trackErrorPageRendered(router));
    } catch (err) {
      // Whelp, we tried.
    }
  }, [dispatch, router, props]);

  if (props.message && (displayProductionErrors || showError)) {
    return <ErrorStack errorProps={props} />;
  }

  return (
    <StyledCheckoutTemplate hideNavigation fullWidth>
      <ErrorPageLayout statusCode={props.statusCode} />
    </StyledCheckoutTemplate>
  );
};

ErrorPage.getInitialProps = async (ctx: NextPageContext) => {
  // Sentry adds the __sentry_captured__ property to error objects to prevent re-sending errors.
  // We manually set this to false to make sure we can capture that this error caused the _error page to show
  if (ctx.err instanceof Error) {
    Object.defineProperty(ctx.err, '__sentry_captured__', {
      // enumerable: false, // the default, so we can save on bundle size by not explicitly setting it
      value: false,
      writable: true,
      configurable: true,
    });
  }

  captureException(ctx.err, {
    tags: {
      uh_oh_page: true,
    },
  });

  const errorInitialProps = await NextErrorComponent.getInitialProps(ctx);

  return {
    ...errorInitialProps,
    message: ctx.err?.message,
    stack: ctx.err?.stack,
    statusCode: errorInitialProps.statusCode,
  };
};

export default createPage(ErrorPage, {
  resourceBundles: ['site_error'],
});
